import React, { useId, useEffect, useState, useCallback } from 'react'
import { Alert, Form, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'
import { useCancelToken } from '../../utils/hooks/useCancelToken'
import SearchField from '../Forms/SearchField'
import Article from './Article'
import ArticleDND from './ArticleDND'
import LazyLoad from '../Forms/LazyLoad'
import SearchResultAmount from '../Forms/SearchResultAmount'
import { makeCall } from '../../utils/api/makeCall'
import useMountEffect from '../../utils/hooks/useMountEffect'

const ArticleSelect = ({
  selectedArticles,
  setSelectedArticles,
  hideArticles,
  withDND,
  getArticleAssignments,
  listRef,
  singleSelection,
  officeIndependent = true,
  localArticleDependent = false,
  userOffices = false,
  maintenance = false,
}) => {
  const [fetchedArticles, setFetchedArticles] = useState([])
  const [searchText, setSearchText] = useState('')
  const [offset, setOffset] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const { t } = useTranslation()
  const { createOrCancelToken, sourceRef, isCancel } = useCancelToken()

  const limit = 10
  const minChars = 1

  const [lastClickedArticleId, setLastClickedArticleId] = useState(null)
  const [allowRange, setAllowRange] = useState(false)
  const [allSelected, setAllSelected] = useState(false)

  const [, setErrorStates] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const [startWithSelectAll, setStartWithSelectAll] = useState(false)

  const [hasSearched, setHasSearched] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  const getParamsForArticlesRequest = useCallback(
    ({ newOffset, newSearch }) => {
      return {
        'filter.searchText': newSearch ? newSearch : null,
        'page.offset': newOffset,
        'page.limit': limit,
        'filter.officeIndependent': !!officeIndependent,
        'filter.localArticleDependent': !!localArticleDependent,
        'filter.userOffices': !!userOffices,
        'filter.maintenance': !!maintenance,
      }
    },
    [limit, officeIndependent, localArticleDependent, userOffices, maintenance]
  )

  const fetchArticles = useCallback(
    async ({ newOffset, newSearch }) => {
      createOrCancelToken()

      const append = !!newOffset
      const params = getParamsForArticlesRequest({ newOffset, newSearch })

      makeCall(
        'getArticles',
        async () => {
          const response = await apiClient.getJson(
            APIEndpoints.articles,
            params,
            true,
            true,
            true,
            { cancelToken: sourceRef.current.token }
          )

          setFetchedArticles((prev) =>
            append ? [...prev, ...response.data] : response.data
          )

          const total = parseInt(response.headers['x-total-count'])
          if (!isNaN(total)) setTotalCount(total)

          setOffset(newOffset + limit)
          setIsSearching(false)
        },
        setErrorStates,
        setLoadingStates,
        isCancel
      )
    },
    [
      createOrCancelToken,
      getParamsForArticlesRequest,
      sourceRef,
      isCancel,
      limit,
    ]
  )

  useEffect(() => {
    const query = (searchText || '').trim()

    if (!query || query.length < minChars) {
      setFetchedArticles([])
      setOffset(0)
      setTotalCount(0)
      setHasSearched(false)
      setIsSearching(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setHasSearched(true)
      setIsSearching(true)
      fetchArticles({ newOffset: 0, newSearch: query })
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [searchText, minChars, fetchArticles])

  const handleChangeSearchText = (newSearchText) => {
    setAllSelected(false)
    setSelectedArticles([])
    setLastClickedArticleId(null)
    setSearchText(newSearchText)
  }

  const filteredArray = hideArticles
    ? fetchedArticles.filter(
        (item) => !hideArticles.find((element) => element === item.article.id)
      )
    : fetchedArticles

  const checkSessionStorage = async () => {
    if (sessionStorage.getItem('searchArticleNumber')) {
      const articleNumber = JSON.parse(
        sessionStorage.getItem('searchArticleNumber')
      )
      sessionStorage.removeItem('searchArticleNumber')

      if (articleNumber) {
        handleChangeSearchText(articleNumber)
        setStartWithSelectAll(true)
      }
    }
  }

  useMountEffect(() => {
    checkSessionStorage()
  })

  function getArticlesInRange(lastClickedId, newId) {
    const lastIndex = filteredArray.findIndex(
      (article) => article.article.id === lastClickedId
    )
    const newIndex = filteredArray.findIndex(
      (article) => article.article.id === newId
    )

    if (lastIndex < newIndex)
      return filteredArray.slice(lastIndex, newIndex + 1)
    if (lastIndex > newIndex)
      return filteredArray.slice(newIndex, lastIndex + 1)

    return []
  }

  const onClickSelectedArticles = (article) => {
    setAllSelected(false)

    const selectedArticleId = article.article.id
    setLastClickedArticleId(selectedArticleId)

    let newSelectedArticles = [...selectedArticles]
    const existingArticleIndex = selectedArticles.findIndex(
      (a) => a.article.id === selectedArticleId
    )

    if (allowRange) {
      const articlesInRange = getArticlesInRange(
        lastClickedArticleId,
        selectedArticleId
      )

      if (selectedArticles.find((a) => a.article.id === lastClickedArticleId)) {
        setSelectedArticles([
          ...new Set([...newSelectedArticles, ...articlesInRange]),
        ])
      }
    } else if (existingArticleIndex >= 0) {
      newSelectedArticles.splice(existingArticleIndex, 1)
      setSelectedArticles(newSelectedArticles)
    } else {
      if (singleSelection) newSelectedArticles = []
      newSelectedArticles.push(article)
      setSelectedArticles(newSelectedArticles)
    }
  }

  useEffect(() => {
    const listRefCurrent = listRef?.current

    const keypressShiftCallback = (e) => {
      if (!singleSelection && e.shiftKey && lastClickedArticleId)
        setAllowRange(true)
      else setAllowRange(false)
    }

    if (listRefCurrent)
      listRefCurrent.addEventListener('click', keypressShiftCallback)

    return () => {
      if (listRefCurrent) {
        listRefCurrent.removeEventListener('click', keypressShiftCallback)
      }
    }
  }, [lastClickedArticleId, listRef, singleSelection])

  const setAllArticles = useCallback(async () => {
    let articles = []

    const params = {
      'filter.searchText': (searchText || '').trim(),
      'filter.officeIndependent': !!officeIndependent,
      'filter.localArticleDependent': !!localArticleDependent,
      'filter.userOffices': !!userOffices,
      'filter.maintenance': !!maintenance,
    }

    await makeCall(
      'getAllArticles',
      async () => {
        articles = await apiClient.getJson(APIEndpoints.articles, params)
      },
      setErrorStates,
      setLoadingStates,
      isCancel
    )

    return articles
  }, [
    searchText,
    officeIndependent,
    localArticleDependent,
    userOffices,
    maintenance,
    isCancel,
  ])

  const selectAll = useCallback(async () => {
    let articles = []
    if (!allSelected) articles = await setAllArticles()

    setSelectedArticles(articles)
    setAllSelected((prev) => !prev)
  }, [allSelected, setSelectedArticles, setAllSelected, setAllArticles])

  useEffect(() => {
    if (startWithSelectAll && searchText) {
      setStartWithSelectAll(false)
      selectAll()
    }
  }, [startWithSelectAll, searchText, selectAll])

  const checkboxId = useId()
  const trimmedSearchText = (searchText || '').trim()
  const isValidSearch = trimmedSearchText.length >= minChars

  const canLazyLoad =
    isValidSearch &&
    filteredArray.length > 0 &&
    totalCount > offset &&
    !loadingStates.getArticles

  return (
    <>
      <Form.Group className='mb-3 p-2'>
        <Form.Label>{t('ProductOverview:searchArticle')}</Form.Label>

        <SearchField
          placeholder={t('ProductOverview:searchArticle')}
          value={searchText}
          onChange={(e) => handleChangeSearchText(e.target.value)}
          onClearClick={() => handleChangeSearchText('')}
        />

        <div className='d-flex justify-content-between'>
          {hasSearched && !isSearching && filteredArray.length > 0 && (
            <SearchResultAmount count={filteredArray.length} />
          )}
          {loadingStates.getAllArticles ? (
            <Spinner
              size='sm'
              className='mt-2'
              animation='border'
              variant='primary'
            />
          ) : (
            filteredArray.length > 0 &&
            !singleSelection && (
              <Form.Check
                className='mt-2'
                label={t('order:selectAll')}
                id={checkboxId}
                checked={allSelected}
                onChange={selectAll}
              />
            )
          )}
        </div>
      </Form.Group>

      {filteredArray.map((article, index) =>
        withDND ? (
          <ArticleDND
            key={article.article.id}
            onClickSelectedArticles={onClickSelectedArticles}
            selectedArticles={selectedArticles}
            article={article}
            index={index}
            getArticleAssignments={getArticleAssignments}
          />
        ) : (
          <Article
            key={article.article.id}
            onClickSelectedArticles={onClickSelectedArticles}
            selectedArticles={selectedArticles}
            article={article}
            index={index}
          />
        )
      )}
      {hasSearched && !isSearching && !filteredArray.length && (
        <Alert variant='info'>
          {t('productsOverview:noProductsAvailable')}
        </Alert>
      )}
      {canLazyLoad ? (
        <LazyLoad
          fetchData={() =>
            fetchArticles({
              newOffset: offset,
              newSearch: (searchText || '').trim(),
            })
          }
          listRef={listRef}
        />
      ) : null}
      {loadingStates.getArticles && (
        <div className='d-flex justify-content-center p-3'>
          <Spinner animation='border' variant='primary' />
        </div>
      )}
    </>
  )
}

export default ArticleSelect
